import { useState } from "react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { trackEvent, AnalyticsEvent } from "@/analytics";

/**
 * Lightweight feedback capture at the end of the SOP report (RES-43), so an
 * unaccompanied chief on the sandbox/demo leaves data instead of anecdotes:
 * a one-click "was this useful?" answer (tracked) plus a mailto link for the
 * free-text "what's missing?" — dependency-free by design. Hidden from print
 * so it never appears in the exported PDF.
 */

// Same contact address the fire-chief sandbox landing page advertises.
export const FEEDBACK_EMAIL = "hello@respai.ca";

const FEEDBACK_MAILTO =
  `mailto:${FEEDBACK_EMAIL}` +
  `?subject=${encodeURIComponent("SOP Compliance preview feedback")}` +
  `&body=${encodeURIComponent("What's missing or wrong in the SOP Compliance preview?\n\n")}`;

export function SopFeedbackBlock() {
  const [answered, setAnswered] = useState(false);

  const answer = (useful: boolean) => {
    trackEvent(AnalyticsEvent.SopFeedbackSubmitted, { useful });
    setAnswered(true);
  };

  return (
    <Card className="print:hidden">
      <CardHeader>
        <CardTitle>Was this useful?</CardTitle>
        <p className="text-sm text-muted-foreground">
          This preview has to earn its place on your dashboard. Tell us whether
          it does.
        </p>
      </CardHeader>
      <CardContent className="flex flex-wrap items-center gap-3">
        {answered ? (
          <p className="text-sm text-muted-foreground">
            Thanks, noted. Anything specific we should add or fix?
          </p>
        ) : (
          <>
            <Button variant="outline" size="sm" onClick={() => answer(true)}>
              Yes, useful
            </Button>
            <Button variant="outline" size="sm" onClick={() => answer(false)}>
              Not really
            </Button>
          </>
        )}
        <a
          href={FEEDBACK_MAILTO}
          className="text-sm text-primary hover:underline"
          onClick={() => trackEvent(AnalyticsEvent.SopFeedbackEmailOpened)}
        >
          What&apos;s missing? Email us
        </a>
      </CardContent>
    </Card>
  );
}
